import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Trans, useTranslation } from 'react-i18next'
import './styles.scss'
import GenericModal from '../../../GenericModal'
import ConflictPair from '../ConflictPair'
import { resolveFactLabel } from '../../../../Default/NotificationCenter/details/factLabel'
import { factConflictActions } from '../../../../../redux/actions/factConflict'
import { ReactComponent as WarningIcon } from '../../../../../assets/img/icons/warning-featured.svg'

// Blocking confirmation shown when a user set would newly contradict another fact
// on the sheet. Driven by the `factConflict` slice, which the factsheet-contradiction
// middleware fills — holding the set back — when the set introduces a conflict.
// "Add anyway" replays the held set; "Cancel" (and ✕ / Esc / overlay) discards it,
// and since nothing was applied there is nothing to undo.
const FactConflictModal = () => {
  const pending = useSelector((state) => state.factConflict.pending)
  const blueprint = useSelector((state) => state.factsheet.blueprint)
  const dispatch = useDispatch()
  const { t } = useTranslation(null, {
    keyPrefix: 'editProperty',
  })

  const clear = () => dispatch(factConflictActions.clearFactConflict())

  // "Add anyway": replay the held set, flagged so the middleware lets it through
  // instead of re-raising this modal.
  const keep = () => {
    const heldAction = pending?.pendingAction
    if (heldAction) {
      dispatch({
        ...heldAction,
        meta: { ...heldAction.meta, skipContradictionCheck: true },
      })
    }
    clear()
  }

  // "Cancel"/close: the set was never applied, so just drop the pending conflict.
  const cancel = () => clear()

  // every pair's `added` names the just-set fact; `pending` always carries at
  // least one pair.
  const addedLabel = pending ? resolveFactLabel(blueprint, pending.pairs[0]?.added) : ''

  const MAX_PAIRS = 2
  const visiblePairs = pending ? pending.pairs.slice(0, MAX_PAIRS) : []
  const remainingCount = pending ? pending.pairs.length - visiblePairs.length : 0

  const body = pending && (
    <div className="FactConflictModal-Body">
      <div className="FactConflictModal-Pairs">
        {visiblePairs.map((pair, index) => (
          <ConflictPair
            key={`${pair.existing}-${pair.added}-${index}`}
            left={{ label: resolveFactLabel(blueprint, pair.existing) }}
            right={{ label: resolveFactLabel(blueprint, pair.added) }}
          />
        ))}
        {remainingCount > 0 && (
          <p className="FactConflictModal-More">
            {t('factsheet.conflictModal.more', { count: remainingCount })}
          </p>
        )}
      </div>
      <p className="FactConflictModal-Description">
        {t('factsheet.conflictModal.description')}
      </p>
      <p className="FactConflictModal-Question">
        <Trans
          t={t}
          i18nKey="factsheet.conflictModal.question"
          values={{ fact: addedLabel }}
          components={{ fact: <span className="FactConflictModal-Question-Fact"/> }}
        />
      </p>
    </div>
  )

  return (
    <GenericModal
      id="fact-conflict-modal"
      className="FactConflictModal"
      show={!!pending}
      onClose={cancel}
      title={t('factsheet.conflictModal.title')}
      icon={<span className="FactConflictModal-Icon"><WarningIcon/></span>}
      iconPlacement="top"
      showCloseButton
      closeButtonLabel={t('factsheet.conflictModal.cancel')}
      maxWidth="400px"
      buttonsCentered={false}
      body={body}
      actions={[
        { label: 'editProperty.factsheet.conflictModal.addAnyway', variant: 'neutral', onClick: keep },
        { label: 'editProperty.factsheet.conflictModal.cancel', variant: 'primary', onClick: cancel },
      ]}
    />
  )
}

export default FactConflictModal
